import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, TrendingUp, Sparkles, Globe, Target, Layers } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { koraAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Phase 27.37.V — Kora chatbot analytics.
 *
 * KR-only dashboard reading `GET /api/kora/analytics`. Renders four
 * top-line stat cards, a language distribution list, top interests,
 * top landing pages and a tiny 7-day trend sparkline.
 */
export default function KoraAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      const r = await koraAPI.analytics();
      setData(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load analytics'));
    } finally { setLoading(false); }
  })(); }, []);

  if (loading) return <p className="p-10 text-center text-sm text-slate-500">Loading…</p>;
  if (!data) return <p className="p-10 text-center text-sm text-slate-500">No data yet.</p>;

  const rate = data.qualification_rate || {};
  const totals = data.totals || {};
  const daily = data.daily_series || [];
  const maxDaily = Math.max(1, ...daily.map((d) => d.sessions));

  return (
    <div data-testid="kora-analytics-page">
      <PageHeader
        title="Kora Analytics"
        description="How the landing-page chatbot is performing across your campaigns."
      />
      <div className="page-content space-y-4">
        {/* Top-line stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={<MessageCircle size={16} />} label="Conversations today" value={totals.today} tone="sky" />
          <Stat icon={<MessageCircle size={16} />} label="This week" value={totals.week} tone="slate" />
          <Stat icon={<Target size={16} />} label="Qualification rate (7d)" value={`${rate.week ?? 0}%`} tone="emerald" />
          <Stat icon={<Sparkles size={16} />} label="Avg msgs per session" value={data.avg_messages_per_session ?? 0} tone="violet" />
        </div>

        {/* 7-day trend */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} className="text-[#b8862d]" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">
                Last 7 days · sessions vs qualified
              </h3>
            </div>
            <div className="flex items-end gap-1 h-32" data-testid="kora-daily-chart">
              {daily.map((d) => {
                const height = (d.sessions / maxDaily) * 100;
                const qHeight = d.sessions ? (d.qualified / d.sessions) * height : 0;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex flex-col justify-end" style={{ height: '100px' }}>
                      <div className="bg-sky-100 rounded-t w-full relative" style={{ height: `${Math.max(4, height)}%` }}>
                        {d.qualified > 0 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-emerald-500 rounded-t" style={{ height: `${(d.qualified / d.sessions) * 100}%` }} />
                        )}
                      </div>
                    </div>
                    <div className="text-[9.5px] text-slate-500">{d.date.slice(5)}</div>
                    <div className="text-[10px] font-medium text-slate-700">{d.sessions}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[10.5px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-100 border border-sky-200 rounded" /> Sessions</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded" /> Qualified</span>
            </div>
          </CardContent>
        </Card>

        {/* Distribution grids */}
        <div className="grid md:grid-cols-2 gap-3">
          <ListCard icon={<Globe size={14} />} title="Language distribution" rows={data.language_distribution} keyField="language" />
          <ListCard icon={<Layers size={14} />} title="Top areas of interest" rows={data.top_areas_of_interest} keyField="area" />
        </div>

        <ListCard icon={<Sparkles size={14} />} title="Most active landing pages" rows={data.top_landing_pages} keyField="slug" />
      </div>
    </div>
  );
}

function Stat({ icon, label, value, tone }) {
  const toneMap = {
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
  };
  return (
    <Card>
      <CardContent className={`p-4 border-l-4 ${toneMap[tone] || toneMap.slate}`}>
        <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-1">
          {icon} {label}
        </div>
        <div className="text-2xl font-semibold text-slate-900">{value ?? 0}</div>
      </CardContent>
    </Card>
  );
}

function ListCard({ icon, title, rows, keyField }) {
  const total = (rows || []).reduce((s, r) => s + (r.count || 0), 0);
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3 text-[#b8862d]">
          {icon}
          <h3 className="text-xs font-semibold uppercase tracking-wider">{title}</h3>
        </div>
        {(!rows || rows.length === 0) ? (
          <p className="text-xs text-slate-500 italic">No data yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((r) => {
              const pct = total ? Math.round((r.count / total) * 100) : 0;
              return (
                <li key={r[keyField]} className="flex items-center justify-between text-sm">
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="text-slate-800 truncate">
                      {keyField === 'language'
                        ? (r.language === 'es' ? 'Español' : r.language === 'en' ? 'English' : r.language)
                        : String(r[keyField])}
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                      <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] text-slate-500">{pct}%</span>
                    <span className="text-sm font-medium text-slate-800 w-8 text-right">{r.count}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
